import { apiGet, apiPost } from './client';
import type { StudentDashboard, PracticeQuestion, AttemptResult } from '../types';

export const fetchStudentDashboard = () => apiGet<StudentDashboard>('/student/dashboard');

export interface PracticeSet { questions: PracticeQuestion[]; locked: number; premiumRequired: boolean }
export const fetchPracticeQuestions = (topicId: number) =>
  apiGet<PracticeSet>(`/student/topics/${topicId}/practice`);

// Plan / premium (4.25)
export const fetchPlan = () => apiGet<{ isPremium: boolean; freePracticeLimit: number; freePyqLimit: number }>('/student/plan');
export const upgradePlan = () => apiPost<{ isPremium: boolean }>('/student/upgrade', {});

export const submitAttempt = (questionId: number, selectedIndex: number) =>
  apiPost<AttemptResult>('/student/attempts', { questionId, selectedIndex });

export const fetchIncorrectQuestions = () =>
  apiGet<Array<{ id: number; text: string; topicId?: number; difficulty?: string }>>('/student/attempts/incorrect');

// --- Bookmarks (4.10) ---
export interface BookmarkRef { entityType: string; entityId: number }
export interface BookmarkItem {
  id: number;
  entityType: string;
  entityId: number;
  label: string;
  createdAt?: string;
  preview?: string | null;
  code?: string | null;
  topicId?: number | null;
  topicTitle?: string | null;
  subjectName?: string | null;
  subjectId?: number | null;
  chapterName?: string | null;
  chapterId?: number | null;
  difficulty?: string | null;
  questionType?: string | null;
  year?: number | null;
  tags?: string[] | null;
  hasExplanation?: boolean;
  isPyq?: boolean;
}
export const fetchBookmarkIds = (type?: string) =>
  apiGet<BookmarkRef[]>(`/student/bookmarks/ids${type ? `?type=${type}` : ''}`);
export const fetchBookmarks = () => apiGet<BookmarkItem[]>('/student/bookmarks');
export const toggleBookmark = (entityType: string, entityId: number) =>
  apiPost<{ bookmarked: boolean }>('/student/bookmarks', { entityType, entityId });

// --- Revision (4.12) ---
export interface RevisionData {
  weakTopics: Array<{ topicId: number; title: string; wrong: number; total: number }>;
  incorrectQuestions: Array<{ id: number; text: string; topicId?: number }>;
  bookmarkedTopics: Array<{ topicId: number; title: string }>;
  flashcards: Array<{ id: number; title: string; content: { front?: string; back?: string } | null }>;
}
export const fetchRevision = () => apiGet<RevisionData>('/student/revision');

// --- Question reporting (5.24) ---
// Mirrors ACTIVE_REASONS in server/models/QuestionReport.js. 'formatting' is a
// legacy value the server still accepts on old rows but no longer offers.
export const REPORT_REASONS = [
  'wrong_answer', 'wrong_options', 'wrong_explanation', 'typo',
  'duplicate', 'incorrect_topic', 'outdated', 'other',
] as const;

export const REPORT_REASON_LABELS: Record<string, string> = {
  wrong_answer: 'Wrong answer key',
  wrong_options: 'Wrong options',
  wrong_explanation: 'Incorrect explanation',
  typo: 'Typographical error',
  duplicate: 'Duplicate question',
  incorrect_topic: 'Incorrect topic',
  outdated: 'Outdated information',
  other: 'Other',
  formatting: 'Formatting issue',
};

export interface MyReport {
  id: number; questionId: number; questionText?: string | null;
  reason: string; detail?: string | null; status: string;
  clarificationRequest?: string | null; createdAt: string; resolvedAt?: string | null;
}
export const fetchMyReports = () => apiGet<MyReport[]>('/student/reports');
export const answerClarification = (id: number, response: string) =>
  apiPost<{ id: number; status: string }>(`/student/reports/${id}/clarify`, { response });
export const reportQuestion = (questionId: number, reason: string, detail?: string) =>
  apiPost<{ id: number; status: string }>(`/student/questions/${questionId}/report`, { reason, detail });

// --- Subscriptions / billing (6.8/6.9) ---
export interface Plan { id: number; code: string; name: string; priceInr: number; durationDays: number | null; features: string[] | null; active: boolean }
export const fetchPlans = () => apiGet<Plan[]>('/student/plans');
export const validateCoupon = (code: string, planId: number) =>
  apiPost<{ valid: boolean; code: string; discountInr: number; finalInr: number }>('/student/coupons/validate', { code, planId });
export const subscribe = (planId: number, couponCode?: string) =>
  apiPost<{ payment: { id: number; amountInr: number; discountInr: number; invoiceNo: string }; subscription: { planCode: string; expiresAt: string | null } }>('/student/subscribe', { planId, couponCode });

export interface RazorpayOrderResponse {
  order_id: string;
  amount: number;
  currency: string;
  planId: number;
  couponCode?: string;
  key_id: string;
}

export const createRazorpayOrder = (planId: number, couponCode?: string) =>
  apiPost<RazorpayOrderResponse>('/student/razorpay/create-order', { planId, couponCode });

export const verifyRazorpayPayment = (payload: {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
  planId: number;
  couponCode?: string;
}) =>
  apiPost<{
    success: boolean;
    payment: { id: number; amountInr: number; discountInr: number; invoiceNo: string };
    subscription: { id: number; planCode: string; expiresAt: string | null };
  }>('/student/razorpay/verify-payment', payload);


// --- Support (6.6) ---
export interface Ticket { id: number; subject: string; category: string; status: string; createdAt: string; updatedAt: string }
export interface TicketMessage { id: number; authorName?: string; isStaff: boolean; body: string; createdAt: string }
export interface TicketDetail extends Ticket { messages: TicketMessage[] }
export const createTicket = (body: { subject: string; category: string; body: string }) => apiPost<Ticket>('/support/tickets', body);
export const fetchTickets = () => apiGet<Ticket[]>('/support/tickets');
export const fetchTicket = (id: number) => apiGet<TicketDetail>(`/support/tickets/${id}`);
export const replyTicket = (id: number, body: string) => apiPost<TicketMessage>(`/support/tickets/${id}/messages`, { body });

// --- Feedback (7.17) ---
export const submitFeedback = (payload: { type: string; message?: string; rating?: number; context?: Record<string, unknown> }) =>
  apiPost<{ id: number; status: string }>('/support/feedback', payload);

// --- PYQs (4.9) ---
export interface TopicPyq { id: number; text: string; options: string[]; difficulty?: string; year?: number; marks?: number; attempted: boolean }
export interface TopicPyqs { years: number[]; questions: TopicPyq[]; locked?: number; premiumRequired?: boolean }
export const fetchTopicPyqs = (topicId: number, filters: { year?: string; difficulty?: string; attempted?: string }) => {
  const p = new URLSearchParams();
  if (filters.year) p.set('year', filters.year);
  if (filters.difficulty) p.set('difficulty', filters.difficulty);
  if (filters.attempted) p.set('attempted', filters.attempted);
  return apiGet<TopicPyqs>(`/student/topics/${topicId}/pyqs${p.toString() ? `?${p}` : ''}`);
};
